// Utility functions for camp data import and enrichment

/**
 * Normalize a URL by trimming, removing trailing slash, and lowercasing.
 */
export const normalizeUrl = (url: string) => url ? url.trim().replace(/\/$/, '').toLowerCase() : '';

/**
 * Normalize a name by trimming and lowercasing.
 */
export const normalizeName = (name: string) => name ? name.trim().toLowerCase() : '';

/**
 * Quote a value for CSV/SQL if it contains special characters.
 */
export const quoteIfNeeded = (val: string) => {
  if (!val) return '';
  // Wrap in double quotes if contains comma, space, or special char
  if (/[ ,()]/.test(val)) {
    const escaped = val.replace(/"/g, '""'); // CSV-safe escape
    return `"${escaped}"`;
  }
  return val;
};

/**
 * Check if a camp is in the skipped_camps table (requires supabase client).
 * @param camp - The camp object to check
 * @param supabase - The Supabase client instance
 */
export const isCampSkipped = async (camp: any, supabase: any) => {
  if (!camp) return false;
  const name = normalizeName(camp.name);
  const website_url = normalizeUrl(camp.website_url || '');
  const slug = camp.slug ? camp.slug.trim().toLowerCase() : '';
  const filters = [];
  if (name) filters.push(`name.ilike.${quoteIfNeeded(name)}`);
  if (website_url) filters.push(`website_url.ilike.${quoteIfNeeded(website_url)}`);
  if (slug) filters.push(`slug.ilike.${quoteIfNeeded(slug)}`);
  if (filters.length === 0) return false;
  const { data, error } = await supabase
    .from('skipped_camps')
    .select('id, name, website_url, slug')
    .or(filters.join(','));
  if (error) return false;
  if (data && data.length > 0) {
    for (const row of data) {
      if (
        (row.name && normalizeName(row.name) === name) ||
        (row.website_url && normalizeUrl(row.website_url) === website_url) ||
        (row.slug && row.slug.trim().toLowerCase() === slug)
      ) {
        return true;
      }
    }
  }
  return false;
};

/**
 * Extracts just the street address portion from a full address string
 * @param fullAddress - The potentially full address string
 * @param city - The city to help identify where to split
 * @param state - The state to help identify where to split
 * @param zipCode - The zip code to help identify where to split
 * @returns Just the street address portion
 */
export const extractStreetAddress = (
  fullAddress: string | null,
  city: string | null,
  state: string | null,
  zipCode: string | null
): string => {
  if (!fullAddress) return '';
  
  // If we don't have city/state info, return the full address as-is
  if (!city && !state) return fullAddress;
  
  // Only try to extract if we're confident there's embedded city/state info
  // Look for patterns that suggest the address contains city/state info:
  // 1. Comma-separated format (e.g., "123 Main St, City, State 12345")
  // 2. Multiple commas suggesting structured address
  // 3. State abbreviation or full state name that's clearly not part of street name
  
  const hasCommas = fullAddress.includes(',');
  const commaCount = (fullAddress.match(/,/g) || []).length;
  
  // If no commas, assume it's already just a street address
  if (!hasCommas) {
    return fullAddress;
  }
  
  // If only one comma, be more careful about extraction
  if (commaCount === 1) {
    // Check if the comma is followed by city/state/zip pattern
    const parts = fullAddress.split(',');
    const afterComma = parts[1].trim();
    
    // If what comes after the comma looks like city/state/zip, extract
    const afterCommaLower = afterComma.toLowerCase();
    const looksLikeCityStateZip = (
      (city?.toLowerCase() && afterCommaLower.includes(city.toLowerCase())) ||
      (state?.toLowerCase() && afterCommaLower.includes(state.toLowerCase())) ||
      (zipCode && afterCommaLower.includes(zipCode))
    );
    
    if (looksLikeCityStateZip) {
      return parts[0].trim();
    } else {
      return fullAddress;
    }
  }
  
  // Multiple commas suggest structured address, extract street portion
  if (commaCount >= 2) {
    const parts = fullAddress.split(',');
    return parts[0].trim();
  }
  
  // If we can't parse it, return the original
  return fullAddress;
};

/**
 * Formats the complete address display with separate fields
 * @param address - The street address (will be cleaned if it contains full address)
 * @param city - The city
 * @param state - The state
 * @param zipCode - The zip code
 * @returns Formatted address string with street on first line, city/state/zip on second
 */
export const formatCampAddress = (
  address: string | null,
  city: string | null,
  state: string | null,
  zipCode: string | null
): string => {
  const streetAddress = extractStreetAddress(address, city, state, zipCode);
  const locationParts = [city, state, zipCode].filter(Boolean);
  
  if (!streetAddress && locationParts.length === 0) return '';
  
  if (!streetAddress) {
    return locationParts.join(', ');
  }
  
  if (locationParts.length === 0) {
    return streetAddress;
  }
  
  return `${streetAddress}\n${locationParts.join(', ')}`;
};

/**
 * Adds UTM tracking parameters to external URLs
 * @param url - The URL to add UTM parameters to
 * @param utmSource - The UTM source (defaults to 'campwhiz.com')
 * @returns URL with UTM parameters appended
 */
export const addUtmParams = (url: string | null | undefined, utmSource: string = 'campwhiz.com'): string => {
  if (!url) return '';
  
  try {
    const urlObj = new URL(url);
    
    // Add UTM parameter if not already present
    if (!urlObj.searchParams.has('utm_source')) {
      urlObj.searchParams.set('utm_source', utmSource);
    }
    
    return urlObj.toString();
  } catch (e) {
    // If URL parsing fails, return the original URL
    return url;
  }
};

/**
 * Convert state abbreviation to full state name
 * @param stateAbbr - State abbreviation (e.g., "FL", "CA") or full name
 * @returns Full state name (e.g., "Florida", "California") or original if not found/already full name
 */
export const convertStateAbbreviationToFullName = (stateAbbr: string | null | undefined): string | null => {
  if (!stateAbbr) return null;
  
  const stateAbbrUpper = stateAbbr.trim().toUpperCase();
  
  const stateMap: Record<string, string> = {
    'AL': 'Alabama', 'AK': 'Alaska', 'AZ': 'Arizona', 'AR': 'Arkansas',
    'CA': 'California', 'CO': 'Colorado', 'CT': 'Connecticut', 'DE': 'Delaware',
    'FL': 'Florida', 'GA': 'Georgia', 'HI': 'Hawaii', 'ID': 'Idaho',
    'IL': 'Illinois', 'IN': 'Indiana', 'IA': 'Iowa', 'KS': 'Kansas',
    'KY': 'Kentucky', 'LA': 'Louisiana', 'ME': 'Maine', 'MD': 'Maryland',
    'MA': 'Massachusetts', 'MI': 'Michigan', 'MN': 'Minnesota', 'MS': 'Mississippi',
    'MO': 'Missouri', 'MT': 'Montana', 'NE': 'Nebraska', 'NV': 'Nevada',
    'NH': 'New Hampshire', 'NJ': 'New Jersey', 'NM': 'New Mexico', 'NY': 'New York',
    'NC': 'North Carolina', 'ND': 'North Dakota', 'OH': 'Ohio', 'OK': 'Oklahoma',
    'OR': 'Oregon', 'PA': 'Pennsylvania', 'RI': 'Rhode Island', 'SC': 'South Carolina',
    'SD': 'South Dakota', 'TN': 'Tennessee', 'TX': 'Texas', 'UT': 'Utah',
    'VT': 'Vermont', 'VA': 'Virginia', 'WA': 'Washington', 'WV': 'West Virginia',
    'WI': 'Wisconsin', 'WY': 'Wyoming', 'DC': 'District of Columbia'
  };
  
  // If it's a 2-letter code that matches, return the full name
  if (stateAbbrUpper.length === 2 && stateMap[stateAbbrUpper]) {
    return stateMap[stateAbbrUpper];
  }
  
  // If it's not a 2-letter code, assume it's already a full name (or invalid)
  // Capitalize properly: "florida" -> "Florida", "NEW YORK" -> "New York"
  if (stateAbbrUpper.length !== 2) {
    return stateAbbr.split(/\s+/).map(word => 
      word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()
    ).join(' ');
  }
  
  // If it's a 2-letter code but not in the map, return original (might be invalid)
  return stateAbbr;
}; 